<template>
  <div v-editable="props.stepData" class="step-form" :data-nextstep="props.stepData.nextStep">
    <div
      v-for="option in props.stepData.options"
      :key="option._uid"
      v-editable="option"
      :class="`${option.component} ${option.type}`"
    >
      <component
        :is="getComponentNameForOptiontype(option.type, option.component)"
        :model-value="inputValues[option.type]"
        :name="option.type"
        :placeholder="option.placeholder"
        :values="option.specialtypeoptions"
        :label="option.label"
        :required="option.required"
        :persistent-hint="option.persistentHint"
        :type="option.type"
        :has-success="!v$[option.type]?.$invalid && v$[option.type]?.$dirty && option.required"
        :has-error="v$[option.type]?.$error"
        :error-message="v$[option.type]?.$errors[0]?.$message"
        @input="setValue(option.type, $event)"
        @blur="v$[option.type]?.$touch()"
      >
        <template #hint>
          <WizcoRichtextResolver v-if="option.hint?.content[0]?.content" :blok="option.hint" />
        </template>
      </component>
    </div>
    <StepFooter :error-message="stepFooterErrorMessage" @click="nextStepBtnClick()" />
  </div>
</template>

<script setup>
import { reactive, computed, onMounted, toValue } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, email, minLength, maxLength, helpers, between } from '@vuelidate/validators'
import StepFooter from '../components/footer/StepFooter.vue'
import { useWizcoStore } from '../stores/wizco'

const props = defineProps({
  stepData: {
    type: Object,
    required: true
  },
  stepUuid: {
    type: String,
    required: false,
    default: ''
  }
})
const wizco = useWizcoStore()
const actualStepdataRelation = wizco.stepData[props.stepUuid]

const phoneNumberCheck = (value) =>
  !helpers.req(value) || value === '' || new RegExp('^([0-9-+_ /()*]{5,40})$', 'g').test(value)
const insuranceNumberCheck = (value) =>
  !helpers.req(value) || new RegExp('^([a-zA-Z]{1})([0-9]{9})$', 'g').test(value)

const getValidationDefinitionForOptiontype = (
  optionType,
  optionComponent,
  optionRequired = false
) => {
  if (optionComponent === 'WizcoStepFormCheckbox') {
    return {
      checked: optionRequired
        ? helpers.withMessage(
            'Um fortzufahren, stimmen Sie bitte zu, indem Sie den Haken setzen.',
            (value) => value !== '' && value !== false
          )
        : false
    }
  }
  switch (optionType) {
    case 'anrede':
    case 'dritte_anrede':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte wählen Sie eine Anrede aus.', required)
          : false
      }
    case 'vorname':
    case 'dritte_vorname':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie den Vornamen ein.', required)
          : false,
        minLength: helpers.withMessage(
          'Der Vorname muss mindestens 2 Buchstaben enthalten.',
          minLength(2)
        ),
        maxLength: helpers.withMessage(
          'Der Vorname kann nur maximal 40 Zeichen lang sein.',
          maxLength(40)
        )
      }
    case 'nachname':
    case 'dritte_nachname':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie den Nachnamen ein.', required)
          : false,
        minLength: helpers.withMessage(
          'Der Nachname muss mindestens 2 Buchstaben enthalten.',
          minLength(2)
        ),
        maxLength: helpers.withMessage(
          'Der Nachname kann nur maximal 80 Zeichen lang sein.',
          maxLength(80)
        )
      }
    case 'email':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie eine E-Mail-Adresse an.', required)
          : false,
        email: helpers.withMessage(
          'Ungültige E-Mail-Adresse: Bitte geben Sie eine korrekte E-Mail-Adresse ein.',
          email
        )
      }
    case 'emailbestaetigung':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie eine E-Mail-Adresse an.', required)
          : false,
        email: helpers.withMessage(
          'Ungültige E-Mail-Adresse: Bitte geben Sie eine korrekte E-Mail-Adresse ein.',
          email
        ),
        equal: helpers.withMessage(
          'Die E-Mail-Adresse ist nicht identisch zu der vorherigen.',
          (value) => !helpers.req(value) || value === actualStepdataRelation.data['email']
        )
      }
    case 'telefonnummer':
      return {
        required: optionRequired
          ? helpers.withMessage(
              'Bitte geben Sie eine Telefonnummer ein, unter der wir Sie für Rückfragen erreichen können.',
              required
            )
          : false,
        maxLength: helpers.withMessage(
          'Die Telefonnummer darf nur max. 17 Zeichen enthalten.',
          maxLength(17)
        ),
        phoneNumberCheck: helpers.withMessage(
          'Die Telefonnummer scheint fehlerhaft zu sein.',
          phoneNumberCheck
        )
      }
    case 'strasse':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie eine Straße ein.', required)
          : false,
        isValid: helpers.withMessage(
          'Bitte geben Sie eine Straße ein.',
          (value) => !helpers.req(value) || value.match(/^[A-Za-z0-9ÄÖÜäöüß.\-/,\s]*$/)
        )
      }
    case 'hausnummer':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie eine Hausnummer ein.', required)
          : false,
        minLength: helpers.withMessage('Bitte geben Sie eine Hausnummer ein.', minLength(1)),
        isValid: helpers.withMessage(
          'Bitte geben Sie eine Hausnummer ein.',
          (value) => !helpers.req(value) || value.match(/^\w[A-Za-z0-9.\-/,\s]*$/)
        )
      }
    case 'postleitzahl':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie eine Postleitzahl an.', required)
          : false,
        between: helpers.withMessage(
          'Bitte geben Sie eine gültige deutsche Postleitzahl ein.',
          between(1000, 99998)
        ),
        length: helpers.withMessage(
          'Bitte geben Sie eine 5-stellige Postleitzahl ein.',
          (value) => !helpers.req(value) || value.length === 5
        )
      }
    case 'ort':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie einen Ort ein.', required)
          : false
      }
    case 'krankenkassenId':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie den Namen der Krankenkasse ein.', required)
          : false,
        insuranceKeyCheck: helpers.withMessage(
          'Bitte versuchen Sie die Krankenkasse erneut aus den vorgegebenen Vorschlägen der Liste (Dropdown) auszuwählen.',
          (value) => !helpers.req(value) || !!value.value
        )
      }
    case 'pflegekassenId':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie den Namen der Pflegekasse ein.', required)
          : false,
        insuranceKeyCheck: helpers.withMessage(
          'Bitte versuchen Sie die Pflegekasse erneut aus den vorgegebenen Vorschlägen der Liste (Dropdown) auszuwählen.',
          (value) => !helpers.req(value) || !!value.value
        )
      }
    case 'bday':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie das Geburtsdatum an.', required)
          : false,
        minValue: helpers.withMessage(
          'Bitte überprüfen Sie Ihre Eingabe.',
          (value) => !helpers.req(value) || new Date(1910, 0, 1) < new Date(value)
        ),
        maxValue: helpers.withMessage(
          'Das angegebene Geburtsdatum liegt in der Zukunft. Bitte überprüfen Sie Ihre Eingaben.',
          (value) => !helpers.req(value) || new Date() > new Date(value)
        )
      }
    case 'versicherungsnummer':
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte geben Sie die Versichertennummer ein.', required)
          : false,
        minLength: helpers.withMessage(
          'Die Versichertennummer muss genau 10 Zeichen betragen.',
          minLength(10)
        ),
        maxLength: helpers.withMessage(
          'Die Versichertennummer muss genau 10 Zeichen betragen.',
          maxLength(10)
        ),
        insuranceNumberCheck: helpers.withMessage(
          'Diese Versichertennummer ist nicht korrekt.',
          insuranceNumberCheck
        )
      }
    default:
      return {
        required: optionRequired
          ? helpers.withMessage('Bitte füllen Sie dieses Feld aus.', required)
          : false
      }
  }
}

const getComponentNameForOptiontype = (optionType, optionComponent) => {
  if (optionComponent === 'WizcoStepFormCheckbox') {
    return 'EnrichedPflegeCheckbox'
  }
  switch (optionType) {
    case 'anrede':
    case 'dritte_anrede':
      return 'EnrichedPflegeRadio'
    case 'krankenkassenId':
    case 'pflegekassenId':
      return 'EnrichedPflegeAutocomplete'
    case 'unterschrift':
      return 'PflegeSignatureInput'
    default:
      return 'EnrichedPflegeInput'
  }
}

const allOptionsValidationDefinitions = {}
const allOptionsValues = {}
props.stepData.options.forEach((option) => {
  if (actualStepdataRelation.data[option.type]) {
    // if presets/ values are already existing
    allOptionsValues[option.type] = actualStepdataRelation.data[option.type]
  } else {
    allOptionsValues[option.type] = ''
    if (option.component === 'WizcoStepFormCheckbox') {
      allOptionsValues[option.type] = false
    }
  }
  allOptionsValidationDefinitions[option.type] = getValidationDefinitionForOptiontype(
    option.type,
    option.component,
    option.required
  )
})
const inputValues = reactive(allOptionsValues)
actualStepdataRelation.data = allOptionsValues
const v$ = useVuelidate(allOptionsValidationDefinitions, inputValues)

const nextStepBtnClick = () => {
  if (!wizco.isActiveStepReady) {
    v$.value.$validate()
    return
  }
  wizco.submit()
}

const stepFooterErrorMessage = computed(() => {
  if (!wizco.isActiveStepReady && v$.value.$error) {
    return 'Ihre Eingabe war teilweise unvollständig oder ungültig. Bitte überprüfen Sie die rot markierten Felder.'
  }
  return ''
})

const setValue = (key, value) => {
  if (value.target?.value !== undefined) {
    value = value.target.value
  }
  inputValues[key] = toValue(value) // value has to be set before any tests/ validations  .. sets value inside of vuelidate for given key
  actualStepdataRelation.data[key] = toValue(value)
  // console.log('setValue + isValid + isFormValid? :>> ', key, value, !v$.value[key].$invalid, !v$.value.$invalid, v$.value[key]);
  wizco.isActiveStepReady = !v$.value.$invalid
}

onMounted(() => {
  wizco.isActiveStepReady = !v$.value.$invalid
})
</script>

<style>
.step-form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  --main-flex-gap: 1.5rem;
  gap: var(--main-flex-gap);
}

.step-form > .WizcoStepFormCheckbox:last-of-type {
  padding-block: 0.75rem;
}

@media (min-width: 576px) {
  .step-form {
    margin: 0 auto -0.75rem;
    max-width: 35rem;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .step-form .WizcoStepFormInput {
    width: 100%;
    --flex-gap: 1.5rem;
  }

  .step-form .WizcoStepFormInput.hausnummer,
  .step-form .WizcoStepFormInput.postleitzahl:has(+ .ort) {
    width: calc(33% - (var(--flex-gap) * 0.5));
  }

  .step-form .WizcoStepFormInput.ort,
  .step-form .WizcoStepFormInput.strasse:has(+ .hausnummer) {
    width: calc(67% - (var(--flex-gap) * 0.5));
  }
}

@media screen and (-webkit-min-device-pixel-ratio: 0) {
  /* prevent auto-zoom in safari fix */
  .step-form :is(select, textarea, input) {
    font-size: 16px !important;
  }
}
</style>
